/** @jsx isolateComponent */

import React, { useState, useEffect } from 'react';

import { Button } from '../../navigation/Button';
import { Icon } from '../../display/Icon';
import { IconButton } from '../../navigation/IconButton';
import { Interpolation } from '@emotion/core';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

type AccordionSize = 'small' | 'large';

type Props = {
  /**
   * Title, shown both when closed and open.
   */
  title: React.ReactNode;

  /**
   * Content, only shown when open.
   */
  children: React.ReactNode;

  /**
   * Optional link, visible as icon next to/under (depends on viewport) the content when open.
   */
  href?: string;

  /**
   * Lets a parent open the `Accordion` on its initial render.
   */
  isInitiallyOpen?: boolean;

  /**
   * Enables Accordion to act as a semi-controlled component.
   * A parent will be able to manage the open state. Clicking/toggling
   * the component will still take precedence until this prop changes again.
   */
  isOpen?: boolean;

  /**
   * When `small`, uses smaller padding and renders any link under instead of next to the content.
   *
   * @default large
   */
  // BREAKING_TODO: Remove `size`. Only `small` will be supported now.
  size?: AccordionSize;

  /**
   * Callback when the accordion is toggled
   */
  onToggle?: ({ isOpen }: { isOpen: boolean }) => void;
};

type DerivedState = {
  isFocused: boolean;
  isHovered: boolean;
  isOpen: boolean;
};

const Styles = labelStyles('Accordion', {
  root: (derivedState: DerivedState) => [
    {
      position: 'relative',
      backgroundColor: Colors.grey000,
      border: `1px solid ${Colors.grey000}`,
      borderRadius: 5,
      transitionProperty: 'box-shadow, border-color, background-color',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
    },
    (derivedState.isFocused || derivedState.isHovered || derivedState.isOpen) &&
      withModifier('highlighted', {
        backgroundColor: Colors.white,
        borderColor: Colors.grey200,
        borderRadius: 10,
        boxShadow: Shadows.elevation5,
      }),
  ],
  summary: ({ size }: { size: AccordionSize }, derivedState: DerivedState) => [
    {
      ...Typography.subtitle700,
      display: 'block',
      color: Colors.grey700,
      fontWeight: 600,
      cursor: 'pointer',
      listStyle: 'none',
      '&::-webkit-details-marker': {
        display: 'none',
      },
      // Remove marker from browser / polyfills
      '&::before': {
        content: '""',
      },
    },
    withModifier(
      size,
      {
        small: {
          padding: '20px 20px 20px 55px',
        },
        large: {
          padding: '25px 70px',
        },
      }[size]
    ),
    derivedState.isOpen &&
      withModifier(`open-${size}`, {
        // swapping padding for bottom so that when closed, you can tap anywhere
        // to open, after which the margin absorbs any (smaller) content margins
        paddingBottom: 0,
        marginBottom: size === 'small' ? 20 : 25,
        color: Colors.black,
      }),
    (derivedState.isFocused || derivedState.isHovered) &&
      withModifier('highlighted', {
        color: Colors.blue500,
      }),
  ],
  content: ({ size }: { size: AccordionSize }) => [
    {
      color: Colors.grey700,
    },
    withModifier(
      size,
      {
        small: {
          margin: 20,
        },
        large: {
          margin: `25px 70px`,
        },
      }[size]
    ),
  ],
  arrow: ({ size }: { size: AccordionSize }) => [
    {
      position: 'absolute',
    },
    withModifier(
      size,
      {
        small: {
          top: 20,
          left: 15,
        },
        large: {
          top: 25,
          left: 25,
        },
      }[size]
    ),
  ],
  fullPageButton: ({ isIconOnly }: { isIconOnly: boolean }) => [
    isIconOnly &&
      withModifier('icon-only', {
        position: 'absolute',
        top: 20,
        right: 20,
      }),
    !isIconOnly &&
      withModifier('full-button', {
        margin: 20,
      }),
  ],
});

const FullPageButton = (props: Props) => {
  const iconName = 'arrowOffsite';
  const isIconOnly = props.size === 'large';

  const button = isIconOnly ? (
    <IconButton
      color="white"
      href={props.href}
      icon={iconName}
      label="Open full page"
      target="_blank"
      tooltipPosition="west"
    />
  ) : (
    <Button
      casing="uppercase"
      color="greyAlt"
      href={props.href}
      iconBefore={iconName}
      isFullWidth={true}
      size="small"
      target="_blank"
    >
      Open full page
    </Button>
  );
  return (
    <div
      css={Styles.fullPageButton({
        isIconOnly,
      })}
    >
      {button}
    </div>
  );
};

const defaultProps = {
  size: 'large' as AccordionSize,
};

/**
 * A styled `<details>` component that opens and closes on click.
 */
export const Accordion = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };

  const {
    children,
    href,
    isInitiallyOpen,
    title,
    size,
    onToggle = () => {},
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(!!isInitiallyOpen);

  const derivedState = {
    isFocused,
    isHovered,
    isOpen,
  };

  useEffect(() => {
    if (props.isOpen !== undefined) {
      setIsOpen(props.isOpen);
    }
  }, [props.isOpen]);

  const handleClick = (e: React.SyntheticEvent<EventTarget, Event>) => {
    e.preventDefault();
    onToggle({ isOpen: !isOpen });

    /**
     * We prevent internal state management to consider multiple controlled Accordions.
     * In this case, an Accordion might toggle states and then re-render based on
     * `isOpen`. With `useEffect`, there will be slight jank when the the effect
     * fires, because the browser has already re-painted.
     *
     * See {Controlled with `isOpen`} in ./Accordion.md for an example.
     */
    if (props.isOpen === undefined) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <details css={Styles.root(derivedState) as Interpolation} open={isOpen}>
      <summary
        css={Styles.summary({ size }, derivedState)}
        onBlur={() => setIsFocused(false)}
        onClick={handleClick}
        onFocus={() => setIsFocused(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
      >
        <span css={Styles.arrow({ size }) as Interpolation}>
          <Icon
            canAcceptPointerEvents={false}
            name={isOpen ? 'arrowSmallUp' : 'arrowSmallDown'}
            size={24}
          />
        </span>
        {title}
      </summary>
      {isOpen && <div css={Styles.content({ size })}>{children}</div>}
      {isOpen && href && <FullPageButton {...props} />}
    </details>
  );
};

Accordion.defaultProps = defaultProps;
